import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import {
    Truck, Package, Users, FileText, MessageSquare,
    LogOut, Menu, X, Plus, Calendar, ShoppingBasket, Tag, Cable, Settings, Rocket, Warehouse, BookOpen, Activity, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';

// Sub-pages
const OrdersPage = lazy(() => import('./OrdersPage'));
const VansPage = lazy(() => import('./VansPage'));
const ClientsPage = lazy(() => import('./ClientsPage'));
const DocumentsPage = lazy(() => import('./DocumentsPage'));
const CommunicationsPage = lazy(() => import('./CommunicationsPage'));
const CatalogPage = lazy(() => import('./CatalogPage'));
const PriceListsPage = lazy(() => import('./PriceListsPage'));
const EasyfattPage = lazy(() => import('./EasyfattPage'));
const CompanySettingsPage = lazy(() => import('./CompanySettingsPage'));
const OnboardingPage = lazy(() => import('./OnboardingPage'));
const WarehousePage = lazy(() => import('./WarehousePage'));
const IntegrationsDashboardPage = lazy(() => import('./IntegrationsDashboardPage'));

const API = `${typeof window !== "undefined" ? window.location.origin : process.env.REACT_APP_BACKEND_URL || ""}/api`;

const navItems = [
    { path: '/admin', icon: Package, label: 'Ordini', exact: true },
    { path: '/admin/vans', icon: Truck, label: 'Autisti' },
    { path: '/admin/warehouse', icon: Warehouse, label: 'Magazzino' },
    { path: '/admin/pricelists', icon: Tag, label: 'Listini' },
    { path: '/admin/catalog', icon: ShoppingBasket, label: 'Catalogo' },
    { path: '/admin/documents', icon: FileText, label: 'Documenti' },
    { path: '/admin/clients', icon: Users, label: 'Clienti' },
    { path: '/admin/communications', icon: MessageSquare, label: 'Comunicazioni' },
    { path: '/admin/easyfatt', icon: Cable, label: 'Gestionale' },
    { path: '/admin/dashboard', icon: Activity, label: 'Analisi Gestionale' },
    { path: '/admin/company', icon: Settings, label: 'Azienda' },
];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const branding = useBranding();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [stats, setStats] = useState({ orders: 0, vans: 0, clients: 0 });
    const [onboardingDone, setOnboardingDone] = useState(null);

    useEffect(() => {
        axios.get(`${API}/company/onboarding`)
            .then((r) => setOnboardingDone(!!r.data.completed))
            .catch(() => setOnboardingDone(true));
    }, []);

    useEffect(() => {
        if (onboardingDone === false && location.pathname === '/admin') {
            navigate('/admin/onboarding', { replace: true });
        }
    }, [onboardingDone, location.pathname, navigate]);

    const items = onboardingDone === false
        ? [{ path: '/admin/onboarding', icon: Rocket, label: 'Avvio' }, ...navItems]
        : navItems;

    const fetchStats = useCallback(async () => {
        try {
            const today = new Date().toISOString().split('T')[0];
            const [ordersRes, vansRes, clientsRes] = await Promise.all([
                axios.get(`${API}/admin/orders?date=${today}`),
                axios.get(`${API}/admin/vans`),
                axios.get(`${API}/admin/clients`)
            ]);
            setStats({
                orders: ordersRes.data.length,
                vans: vansRes.data.length,
                clients: clientsRes.data.length
            });
        } catch (err) {
            console.error('Error fetching stats', err);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
        toast.success('Logout effettuato');
    };

    const isActive = (path, exact = false) => {
        if (exact) return location.pathname === path;
        return location.pathname.startsWith(path);
    };

    return (
        <div className="min-h-screen bg-[#F2F5F2]">
            {/* Mobile header */}
            <div className="md:hidden bg-white border-b border-[#CBD5E1] px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {branding.logo_url ? (
                        <img src={branding.logo_url} alt="Logo" className="h-8 w-8 object-contain rounded" />
                    ) : (
                        <Truck className="h-6 w-6" style={{ color: branding.brand_color }} />
                    )}
                    <span className="font-semibold text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>{branding.name}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
                    {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </Button>
            </div>

            <div className="flex">
                {/* Sidebar */}
                <aside className={`
                    fixed md:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-[#CBD5E1]
                    transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
                    transition-transform duration-200 ease-in-out
                `}>
                    <div className="h-full flex flex-col">
                        <div className="hidden md:flex items-center gap-2 p-6 border-b border-[#CBD5E1]">
                            {branding.logo_url ? (
                                <img src={branding.logo_url} alt="Logo" className="h-10 w-10 object-contain rounded" />
                            ) : (
                                <Truck className="h-8 w-8" style={{ color: branding.brand_color }} />
                            )}
                            <span className="font-bold text-lg text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>{branding.name}</span>
                        </div>

                        <nav className="flex-1 p-4 space-y-1">
                            {items.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    data-testid={`nav-${item.label.toLowerCase()}`}
                                    onClick={() => setSidebarOpen(false)}
                                    style={isActive(item.path, item.exact) ? { backgroundColor: branding.brand_color, color: 'white' } : {}}
                                    className={`
                                        flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                                        ${isActive(item.path, item.exact) 
                                            ? '' 
                                            : 'text-[#64748B] hover:bg-[#F2F5F2] hover:text-[#0F172A]'}
                                    `}
                                >
                                    <item.icon className="h-5 w-5" />
                                    <span className="font-medium">{item.label}</span>
                                </Link>
                            ))}
                        </nav>

                        <div className="p-4 border-t border-[#CBD5E1]">
                            <a
                                href="/manuale"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="nav-manuale"
                                className="flex items-center gap-3 px-4 py-2 mb-1 rounded-lg text-[#64748B] hover:bg-[#F2F5F2] hover:text-[#0F172A] transition-all duration-200"
                            >
                                <BookOpen className="h-5 w-5" />
                                <span className="font-medium">Manuale d'uso</span>
                            </a>
                            <div className="flex items-center gap-3 px-4 py-2 mb-2">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium" style={{ backgroundColor: branding.brand_color }}>
                                    {user?.name?.charAt(0) || 'A'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-[#0F172A] truncate">{user?.name}</p>
                                    <p className="text-xs text-[#64748B]">Amministratore</p>
                                </div>
                            </div>
                            <Link to="/change-password">
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start text-[#64748B] hover:text-brand hover:bg-green-50 mb-1"
                                    data-testid="admin-change-password-btn"
                                >
                                    <KeyRound className="h-5 w-5 mr-3" />
                                    Cambia Password
                                </Button>
                            </Link>
                            <Button 
                                variant="ghost" 
                                className="w-full justify-start text-[#64748B] hover:text-[#B71C1C] hover:bg-red-50"
                                onClick={handleLogout}
                                data-testid="logout-btn"
                            >
                                <LogOut className="h-5 w-5 mr-3" />
                                Esci
                            </Button>
                        </div>
                    </div>
                </aside>

                {/* Overlay for mobile */}
                {sidebarOpen && (
                    <div 
                        className="fixed inset-0 bg-black/20 z-40 md:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Main content */}
                <main className="flex-1 min-w-0 p-6 md:p-8 min-h-screen">
                    {/* Top stats row - only on main orders page */}
                    {location.pathname === '/admin' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                            <Card className="border border-[#CBD5E1] shadow-none card-hover">
                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                    <CardTitle className="text-sm font-medium text-[#64748B]">Ordini Oggi</CardTitle>
                                    <Package className="h-5 w-5 text-brand" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                                        {stats.orders}
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="border border-[#CBD5E1] shadow-none card-hover">
                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                    <CardTitle className="text-sm font-medium text-[#64748B]">Autisti Attivi</CardTitle>
                                    <Truck className="h-5 w-5 text-brand" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                                        {stats.vans}
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="border border-[#CBD5E1] shadow-none card-hover">
                                <CardHeader className="flex flex-row items-center justify-between pb-2">
                                    <CardTitle className="text-sm font-medium text-[#64748B]">Clienti</CardTitle>
                                    <Users className="h-5 w-5 text-brand" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                                        {stats.clients}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    <Suspense fallback={<div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#94A3B8] animate-spin" /></div>}>
                    <Routes>
                        <Route index element={<OrdersPage onStatsUpdate={fetchStats} />} />
                        <Route path="dashboard" element={<IntegrationsDashboardPage />} />
                        <Route path="vans" element={<VansPage />} />
                        <Route path="clients" element={<ClientsPage />} />
                        <Route path="documents" element={<DocumentsPage />} />
                        <Route path="communications" element={<CommunicationsPage />} />
                        <Route path="catalog" element={<CatalogPage />} />
                        <Route path="warehouse" element={<WarehousePage />} />
                        <Route path="pricelists" element={<PriceListsPage />} />
                        <Route path="easyfatt" element={<EasyfattPage />} />
                        <Route path="company" element={<CompanySettingsPage />} />
                        <Route path="onboarding" element={<OnboardingPage />} />
                    </Routes>
                    </Suspense>
                </main>
            </div>
        </div>
    );
}
